import { useEffect } from "react";
import { useAdminUserUsage } from "../hooks/useApi";
import AdminUsageSeriesChart, { type AdminUserDailyUsage } from "./AdminUsageSeriesChart";
import { formatNumber, toPersianDigits } from "../utils/format";

interface AdminUserDrawerProps {
  user: Record<string, unknown> | null;
  onClose: () => void;
}

function formatDate(dateStr?: unknown): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr as string);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fa-IR", { timeZone: "Asia/Tehran" });
}

function StatusBadge({ status }: { status?: string }) {
  switch (status) {
    case "active":
      return <span className="admin-badge admin-badge-success">فعال</span>;
    case "inactive":
      return <span className="admin-badge admin-badge-warning">غیرفعال</span>;
    case "banned":
      return <span className="admin-badge admin-badge-error">مسدود</span>;
    case "deleted":
      return <span className="admin-badge admin-badge-info">حذف‌شده</span>;
    default:
      return <span className="admin-badge admin-badge-info">{status || "—"}</span>;
  }
}

function RoleBadges({ roles }: { roles?: unknown }) {
  const list = Array.isArray(roles) ? (roles as { name?: string; display_name?: string }[]) : [];
  if (list.length === 0) return <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {list.map((r, i) => (
        <span
          key={i}
          className="admin-badge"
          style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary-dark)", fontSize: 11 }}
        >
          {r.display_name || r.name || "—"}
        </span>
      ))}
    </div>
  );
}

function Row({ label, value, ltr = false }: { label: string; value: React.ReactNode; ltr?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>{label}</span>
      <span
        className="text-sm font-medium text-right"
        style={{ color: "var(--admin-text-primary)" }}
        dir={ltr ? "ltr" : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="admin-card overflow-hidden">
      <div className="admin-card-header">
        <h4 className="admin-card-title">{title}</h4>
      </div>
      <div className="p-4 divide-y" style={{ borderColor: "var(--admin-border-light)" }}>
        {children}
      </div>
    </div>
  );
}

export default function AdminUserDrawer({ user, onClose }: AdminUserDrawerProps) {
  useEffect(() => {
    if (!user) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [user]);

  // Hook call must stay above the early return: the drawer stays mounted and
  // ``user`` toggles null <-> user, so calling this only when ``user`` is set
  // would change the hook count between renders and crash the whole page.
  // Passing ``null`` (instead of NaN) keeps the query disabled while closed.
  const rawUserId = user ? (user.id as string | number | undefined) : undefined;
  const { data: usage, isLoading: usageLoading } = useAdminUserUsage(
    rawUserId != null ? Number(rawUserId) : null
  );

  if (!user) return null;

  const isLegal = (user.person_type as string) === "legal";
  const fullName = [user.name, user.family_name].filter(Boolean).join(" ") || "";
  const displayName = isLegal ? (user.company_name as string) || fullName || "" : fullName || "";
  const label = displayName || (user.username as string) || (user.email as string) || `کاربر #${user.id}`;
  const initials = String(label).charAt(0).toUpperCase();
  const emailVerified = Boolean(user.email_verified);
  const phoneVerified = Boolean(user.phone_verified);
  const sub = (user.subscription as Record<string, unknown>) || null;
  const fixedSub = (user.subscription_fixed as Record<string, unknown>) || null;

  const usagePayload = (usage as Record<string, unknown>) || {};
  const totalCost = Number(usagePayload.total_cost ?? 0) || 0;

  const initialsColor = (user.status as string) === "banned"
    ? "var(--admin-error)"
    : (user.status as string) === "deleted"
      ? "var(--admin-text-tertiary)"
      : "var(--admin-primary)";

  return (
    <div className="admin-overlay" role="dialog" aria-modal="true">
      {/* Click backdrop to close */}
      <div className="absolute inset-0 animate-admin-fade-in" onClick={onClose} aria-hidden="true" />

      {/* Drawer panel — slides in from the left up to the middle of the page */}
      <aside className="admin-drawer-panel animate-drawer-left absolute top-0 bottom-0 left-0 w-full md:w-1/2 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: "var(--admin-border)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
              style={{ background: initialsColor }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold truncate" style={{ color: "var(--admin-text-primary)" }}>
                  {label}
                </h3>
                <StatusBadge status={user.status as string} />
              </div>
              <code dir="ltr" className="font-mono text-xs block" style={{ color: "var(--admin-text-secondary)" }}>
                #{String(user.id ?? "—")}
              </code>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors hover:opacity-70 shrink-0"
            style={{ color: "var(--admin-text-tertiary)" }}
            aria-label="بستن"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Identity / Profile */}
          <Section title="هویت (پروفایل)">
            <Row label="نوع هویت" value={isLegal ? "حقوقی" : "حقیقی"} />
            {isLegal ? (
              <>
                <Row label="نام شرکت" value={user.company_name ? (user.company_name as string) : "—"} />
                <Row label="شناسه ملی" value={user.national_id ? toPersianDigits(user.national_id as string) : "—"} ltr={Boolean(user.national_id)} />
              </>
            ) : (
              <>
                <Row label="نام" value={user.name ? (user.name as string) : "—"} />
                <Row label="نام خانوادگی" value={user.family_name ? (user.family_name as string) : "—"} />
                <Row label="کد ملی" value={user.national_code ? toPersianDigits(user.national_code as string) : "—"} ltr={Boolean(user.national_code)} />
              </>
            )}
            <Row label="نام کاربری" value={user.username ? (user.username as string) : "—"} ltr={!user.username} />
            <Row
              label="ایمیل"
              value={
                <span className="inline-flex items-center gap-2">
                  <span dir="ltr">{user.email ? (user.email as string) : "—"}</span>
                  {emailVerified ? <span className="admin-badge admin-badge-success" style={{ fontSize: 10 }}>تأیید شده</span> : null}
                </span>
              }
              ltr={!user.email}
            />
            <Row
              label="شماره موبایل"
              value={
                <span className="inline-flex items-center gap-2">
                  <span dir="ltr">{user.phone ? toPersianDigits(user.phone as string) : "—"}</span>
                  {phoneVerified ? <span className="admin-badge admin-badge-success" style={{ fontSize: 10 }}>تأیید شده</span> : null}
                </span>
              }
              ltr={!user.phone}
            />
            <Row label="کد پستی" value={user.postal_code ? toPersianDigits(user.postal_code as string) : "—"} ltr={Boolean(user.postal_code)} />
          </Section>

          {/* Account */}
          <Section title="حساب کاربری">
            <Row
              label="وضعیت"
              value={<StatusBadge status={user.status as string} />}
            />
            <Row label="مدیر سیستم" value={Boolean(user.is_admin) ? "مدیر" : "—"} />
            <Row label="نقش‌ها" value={<RoleBadges roles={user.roles} />} />
            <Row
              label="اشتراک"
              value={
                sub ? (
                  <span className="inline-flex items-center gap-2">
                    <span>{(sub.plan_name as string) || "—"}</span>
                    {Boolean(sub.is_free) && <span className="admin-badge" style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-secondary)", fontSize: 10 }}>رایگان</span>}
                  </span>
                ) : (
                  <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>
                )
              }
            />
            {sub && sub.start_date ? (
              <Row label="شروع اشتراک" value={formatDate(sub.start_date)} />
            ) : null}
            {sub && sub.end_date ? (
              <Row label="پایان اشتراک" value={formatDate(sub.end_date)} />
            ) : null}
            <Row
              label="موجودی کیف پول"
              value={
                <span className="font-number">
                  {formatNumber(user.wallet_balance as number)} <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>ریال</span>
                </span>
              }
            />
            <Row label="تاریخ عضویت" value={formatDate(user.created_at)} />
            <Row label="آخرین به‌روزرسانی" value={formatDate(user.updated_at)} />
          </Section>

          {fixedSub && (
            <Section title="اشتراک تعرفه ثابت">
              <Row label="پلن" value={(fixedSub.plan_name as string) || "—"} />
              <Row
                label="کلید تخصیص‌یافته"
                value={fixedSub.codecraft_key_number != null ? `CODECRAFT_API_KEY${fixedSub.codecraft_key_number}` : "—"}
                ltr
              />
              <Row label="شروع اشتراک" value={formatDate(fixedSub.start_date)} />
              <Row label="پایان اشتراک" value={formatDate(fixedSub.end_date)} />
            </Section>
          )}

          {/* Usage — PAYG (/v1) totals + usage-in-time chart (like the API-keys tab) */}
          <Section title="مصرف API (پلن مصرفی /v1)">
            <Row
              label="کل درخواست‌ها"
              value={usageLoading ? "…" : toPersianDigits(formatNumber(Number(usagePayload.total_requests) || 0))}
            />
            <Row
              label="کل توکن‌ها"
              value={usageLoading ? "…" : toPersianDigits(formatNumber(Number(usagePayload.total_tokens) || 0))}
            />
            <Row
              label="هزینه کل"
              value={
                usageLoading ? (
                  "…"
                ) : (
                  <span className="font-number">
                    {toPersianDigits(formatNumber(totalCost))}
                    <span className="text-xs mr-1" style={{ color: "var(--admin-text-tertiary)" }}>ریال</span>
                  </span>
                )
              }
            />
            <div className="pt-3 pb-1">
              <p className="text-xs mb-2.5" style={{ color: "var(--admin-text-secondary)" }}>
                روند مصرف در زمان
              </p>
              <AdminUsageSeriesChart
                daily={(usagePayload.daily as AdminUserDailyUsage[]) || []}
                loading={usageLoading}
              />
            </div>
          </Section>

          {/* Provisioning / OpenRouter */}
          <Section title="کلید OpenRouter و پراوژنینگ">
            <Row label="backfill_status" value={user.backfill_status ? (user.backfill_status as string) : "—"} ltr={Boolean(user.backfill_status)} />
            <Row label="openrouter_key_status" value={user.openrouter_key_status ? (user.openrouter_key_status as string) : "—"} ltr={Boolean(user.openrouter_key_status)} />
            <Row
              label="has_hash"
              value={
                Boolean(user.has_hash) ? (
                  <span className="admin-badge admin-badge-success">دارد</span>
                ) : (
                  <span className="admin-badge" style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-tertiary)" }}>ندارد</span>
                )
              }
            />
            {user.openrouter_key_hash ? (
              <Row
                label="openrouter_key_hash"
                value={<span dir="ltr" className="font-mono text-xs block max-w-[220px] truncate">{user.openrouter_key_hash as string}</span>}
              />
            ) : null}
          </Section>
        </div>
      </aside>
    </div>
  );
}
